/**
 * SearchPalette — reusable floating command-palette / search panel.
 *
 * A centered modal with a search input and a flat or grouped item list.
 * Items are navigable with arrow keys and confirmable with Enter.
 * Pointer hover also updates the active highlight.
 *
 * Used for operator search (F3), and extensible to "Add Object",
 * material picker, or any other searchable list context.
 */

import { X } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IconButton } from "./Button.tsx";
import { getSelectableRowStyle } from "./selectableRow.ts";
import { Kbd, SearchField, SectionLabel, useOutsideClick } from "./shared.tsx";
import { GLASS_PANEL } from "./styles.ts";
import {
  COLOR_TEXT_DISABLED,
  COLOR_TEXT_FAINT,
  COLOR_TEXT_SECONDARY,
  COLOR_TEXT_SUBTLE,
  FONT_SIZE_SECTION,
  FONT_SIZE_SMALL,
  RADIUS_CONTROL,
  Z_MODAL,
} from "./tokens.ts";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SearchPaletteItem {
  /** Unique identifier — passed to onSelect. */
  id: string;
  /** Display label. */
  label: string;
  /** Optional icon rendered before the label. */
  icon?: ReactNode;
  /** Optional keyboard shortcut hint shown on the right. */
  shortcut?: string;
  /** Optional group header this item belongs to. */
  group?: string;
  /** Whether the item is selectable (default true). */
  disabled?: boolean;
  /** Arbitrary slot rendered between icon and label (e.g. a star toggle). */
  prefix?: ReactNode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SearchPalette({
  open,
  onClose,
  title,
  placeholder = "Search…",
  items,
  emptyLabel = "No results",
  onSelect,
  width = 320,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  placeholder?: string;
  items: SearchPaletteItem[];
  emptyLabel?: string;
  /** Called when an item is confirmed (click or Enter). */
  onSelect: (id: string) => void;
  width?: number;
}) {
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Filter items by query
  const filtered = useMemo(() => {
    const lq = query.toLowerCase().trim();
    if (!lq) return items;
    return items.filter(
      (it) => it.label.toLowerCase().includes(lq) || it.id.includes(lq),
    );
  }, [items, query]);

  // Selectable subset (for arrow key navigation)
  const selectable = useMemo(
    () => filtered.filter((it) => !it.disabled),
    [filtered],
  );

  // Reset state on open
  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      requestAnimationFrame(() => inputRef.current?.focus());
    }
  }, [open]);

  // Clamp activeIdx when filtered list shrinks
  useEffect(() => {
    setActiveIdx((prev) => Math.min(prev, Math.max(0, selectable.length - 1)));
  }, [selectable.length]);

  // Scroll the active item into view
  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(
      "[data-active='true']",
    ) as HTMLElement | null;
    el?.scrollIntoView({ block: "nearest" });
  }, []);

  // Close on outside click
  useOutsideClick(panelRef, onClose, open);

  // Keyboard: Escape, ArrowUp/Down, Enter
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "Escape":
          e.preventDefault();
          e.stopPropagation();
          onClose();
          break;
        case "ArrowDown":
          e.preventDefault();
          e.stopPropagation();
          setActiveIdx((prev) => Math.min(prev + 1, selectable.length - 1));
          break;
        case "ArrowUp":
          e.preventDefault();
          e.stopPropagation();
          setActiveIdx((prev) => Math.max(prev - 1, 0));
          break;
        case "Enter":
          e.preventDefault();
          e.stopPropagation();
          if (selectable[activeIdx]) onSelect(selectable[activeIdx].id);
          break;
        default:
          // Block non-modifier keys from leaking to the global dispatcher,
          // but allow Cmd/Ctrl combos (e.g. Cmd+Z) to pass through.
          if (!(e.metaKey || e.ctrlKey)) e.stopPropagation();
          break;
      }
    },
    [onClose, onSelect, selectable, activeIdx],
  );

  if (!open) return null;

  // Group items for display (preserving flat order when ungrouped)
  const groups: { label: string | null; items: SearchPaletteItem[] }[] = [];
  let currentGroup: string | null | undefined;
  for (const it of filtered) {
    const g = it.group ?? null;
    if (g !== currentGroup || groups.length === 0) {
      groups.push({ label: g, items: [] });
      currentGroup = g;
    }
    groups[groups.length - 1].items.push(it);
  }

  return (
    <div
      ref={panelRef}
      onKeyDown={handleKeyDown}
      style={{
        position: "fixed",
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        width,
        maxHeight: "70vh",
        ...GLASS_PANEL,
        padding: "12px 14px",
        zIndex: Z_MODAL,
        pointerEvents: "auto",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span
          style={{
            flex: 1,
            fontSize: FONT_SIZE_SECTION,
            color: COLOR_TEXT_FAINT,
            textTransform: "uppercase",
            letterSpacing: 0.8,
          }}
        >
          {title}
        </span>
        <IconButton
          onClick={onClose}
          icon={<X size={14} />}
          label="Close search"
          size={24}
          style={{
            background: "transparent",
            color: COLOR_TEXT_FAINT,
            padding: 0,
          }}
        />
      </div>

      {/* Search input */}
      <SearchField
        value={query}
        onChange={setQuery}
        placeholder={placeholder}
        inputRef={inputRef}
      />

      {/* Item list */}
      <div
        ref={listRef}
        style={{
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        {groups.map((g, gi) => (
          <div key={g.label ?? gi}>
            {g.label && <SectionLabel>{g.label}</SectionLabel>}
            {g.items.map((it) => {
              const sIdx = selectable.indexOf(it);
              const isActive = sIdx >= 0 && sIdx === activeIdx;
              return (
                <div
                  key={it.id}
                  data-active={isActive}
                  onClick={it.disabled ? undefined : () => onSelect(it.id)}
                  onPointerEnter={() => {
                    if (sIdx >= 0) setActiveIdx(sIdx);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "5px 8px",
                    borderRadius: RADIUS_CONTROL,
                    background: getSelectableRowStyle(isActive).background,
                    opacity: it.disabled ? 0.45 : 1,
                    cursor: it.disabled ? "default" : "pointer",
                  }}
                >
                  {it.prefix}
                  {it.icon && (
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        flexShrink: 0,
                        color: it.disabled
                          ? COLOR_TEXT_DISABLED
                          : COLOR_TEXT_SUBTLE,
                      }}
                    >
                      {it.icon}
                    </span>
                  )}
                  <span
                    style={{
                      flex: 1,
                      fontSize: FONT_SIZE_SMALL,
                      color: it.disabled
                        ? COLOR_TEXT_DISABLED
                        : COLOR_TEXT_SECONDARY,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {it.label}
                  </span>
                  {it.shortcut && <Kbd shortcut={it.shortcut} />}
                </div>
              );
            })}
          </div>
        ))}
        {filtered.length === 0 && (
          <div
            style={{
              fontSize: FONT_SIZE_SMALL,
              color: COLOR_TEXT_FAINT,
              padding: "8px 0",
              textAlign: "center",
            }}
          >
            {emptyLabel}
          </div>
        )}
      </div>
    </div>
  );
}
